// 列定义驱动的数据表格（后台规范 §3.1）：td 不换行、外层横向滚动；「显示字段」选择存 localStorage
import { useCallback, useState, type ReactNode } from "react";

export interface Column<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  /** fixed = 这列不能被隐藏（不是冻结列） */
  fixed?: boolean;
  /** 单元格标色类名（cell-warn / cell-bad / cell-alert） */
  tone?: (row: T) => string | undefined;
}

function readHidden(key: string): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** 隐藏了哪些列：读写 localStorage[key]，异常时退回「全部显示」 */
export function useHiddenColumns(key: string): [Set<string>, (col: string) => void] {
  const [hidden, setHidden] = useState(() => new Set(readHidden(key)));
  const toggle = useCallback(
    (col: string) => {
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(col)) next.delete(col);
        else next.add(col);
        try {
          localStorage.setItem(key, JSON.stringify([...next]));
        } catch {
          /* 存不了就只在本次会话生效 */
        }
        return next;
      });
    },
    [key],
  );
  return [hidden, toggle];
}

export function ColumnPicker<T>({ columns, hidden, onToggle }: { columns: Column<T>[]; hidden: Set<string>; onToggle: (key: string) => void }) {
  return (
    <details className="col-picker" data-testid="col-picker">
      <summary>显示字段 ▾</summary>
      <div className="menu">
        {columns.map((c) => (
          <label key={c.key} className="check">
            <input type="checkbox" checked={c.fixed || !hidden.has(c.key)} disabled={c.fixed} onChange={() => onToggle(c.key)} />
            {c.label}
          </label>
        ))}
      </div>
    </details>
  );
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  hidden?: Set<string>;
  onRowClick?: (row: T) => void;
  /** 行上的额外属性（data-* 等，测试和样式用） */
  rowAttrs?: (row: T) => Record<string, string | number>;
  /** 行的额外 class（停用 / 作废行用 void，整行字变灰） */
  rowClass?: (row: T) => string | undefined;
  empty?: ReactNode;
}

export function DataTable<T>({ columns, rows, rowKey, hidden, onRowClick, rowAttrs, rowClass, empty }: DataTableProps<T>) {
  const shown = columns.filter((c) => c.fixed || !hidden?.has(c.key));
  if (!rows.length) return <div className="empty">{empty ?? "没有数据"}</div>;
  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            {shown.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={rowKey(r)}
              className={[onRowClick ? "clickable" : "", rowClass?.(r) ?? ""].filter(Boolean).join(" ") || undefined}
              onClick={onRowClick ? () => onRowClick(r) : undefined} {...rowAttrs?.(r)}>
              {shown.map((c) => (
                <td key={c.key} className={c.tone?.(r)} data-col={c.key}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
